/**
 * Offline Context & Hooks
 * Provides offline state management throughout the app
 * Safe implementation that doesn't cause module initialization issues
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';

const OfflineContext = createContext(null);

// Lazy-load the storage service to avoid initialization issues
let offlineStoragePromise = null;
const getOfflineStorage = async () => {
  if (!offlineStoragePromise) {
    offlineStoragePromise = import('../services/offlineStorage').then(m => m.offlineStorage);
  }
  return offlineStoragePromise;
};

let offlineApiPromise = null;
const getOfflineApi = async () => {
  if (!offlineApiPromise) {
    offlineApiPromise = import('../services/offlineApi').then(m => m.default);
  }
  return offlineApiPromise;
};

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingActions, setPendingActions] = useState(0);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showOfflineBanner, setShowOfflineBanner] = useState(false);
  const [recentlyReconnected, setRecentlyReconnected] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const storageRef = useRef(null);
  const apiRef = useRef(null);

  // Initialize offline services
  useEffect(() => {
    let mounted = true;
    let removeListener = null;

    const initialize = async () => {
      try {
        const storage = await getOfflineStorage();
        const api = await getOfflineApi();
        
        if (!mounted) return;
        
        storageRef.current = storage;
        apiRef.current = api;
        
        await storage.init();

        removeListener = storage.addListener((event) => {
          if (!mounted) return;
          
          switch (event.type) {
            case 'online':
              setIsOnline(true);
              setShowOfflineBanner(false);
              setRecentlyReconnected(true);
              setTimeout(() => {
                if (mounted) setRecentlyReconnected(false);
              }, 5000);
              break;
            case 'offline':
              setIsOnline(false);
              setShowOfflineBanner(true);
              break;
            case 'queued':
              setPendingActions(prev => prev + 1);
              break;
            case 'sync_start':
              setSyncStatus('syncing');
              break;
            case 'sync_complete':
              setSyncStatus(event.failed > 0 ? 'error' : 'synced');
              setLastSyncTime(new Date());
              loadPendingCount();
              setTimeout(() => {
                if (mounted) setSyncStatus('idle');
              }, 3000);
              break;
            default:
              break;
          }
        });

        // Initial load of pending actions
        const queue = await storage.getQueue();
        if (mounted) {
          setPendingActions(queue.length);
          setInitialized(true);
        }
      } catch (err) {
        logger.error('Failed to initialize offline services:', err);
        if (mounted) {
          setInitialized(true); // Still mark as initialized so app works
        }
      }
    };

    initialize();

    // Native online/offline listeners as fallback
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineBanner(false);
      setRecentlyReconnected(true);
      setTimeout(() => {
        if (mounted) setRecentlyReconnected(false);
      }, 5000);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineBanner(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      mounted = false;
      if (removeListener) removeListener();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load pending actions count
  const loadPendingCount = useCallback(async () => {
    try {
      const storage = storageRef.current || await getOfflineStorage();
      const queue = await storage.getQueue();
      setPendingActions(queue.length);
    } catch (err) {
      logger.error('Failed to load pending count:', err);
    }
  }, []);

  // Force sync
  const forceSync = useCallback(async () => {
    if (!isOnline) return false;
    try {
      setSyncStatus('syncing');
      const storage = storageRef.current || await getOfflineStorage();
      await storage.processQueue();
      return true;
    } catch (err) {
      logger.error('Force sync failed:', err);
      return false;
    }
  }, [isOnline]);

  // Prefetch data for offline
  const prefetchData = useCallback(async () => {
    if (!isOnline) return { success: false, message: 'Cannot prefetch while offline' };
    try {
      const api = apiRef.current || await getOfflineApi();
      return api.prefetch();
    } catch (err) {
      logger.error('Prefetch failed:', err);
      return { success: false, message: err.message };
    }
  }, [isOnline]);

  // Clear offline data
  const clearData = useCallback(async () => {
    try {
      const api = apiRef.current || await getOfflineApi();
      await api.clearCache();
      await loadPendingCount();
    } catch (err) {
      logger.error('Clear data failed:', err);
    }
  }, [loadPendingCount]);

  // Dismiss offline banner
  const dismissBanner = useCallback(() => {
    setShowOfflineBanner(false);
  }, []);

  const value = {
    isOnline,
    isOffline: !isOnline,
    pendingActions,
    syncStatus,
    lastSyncTime,
    showOfflineBanner,
    recentlyReconnected,
    initialized,
    forceSync,
    prefetchData,
    clearData,
    dismissBanner,
    refreshPending: loadPendingCount
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}

/**
 * Hook to access offline state
 */
export function useOffline() {
  const context = useContext(OfflineContext);
  if (!context) {
    // Return default values instead of throwing
    return {
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
      pendingActions: 0,
      syncStatus: 'idle',
      lastSyncTime: null,
      showOfflineBanner: false,
      recentlyReconnected: false,
      initialized: false,
      forceSync: () => Promise.resolve(false),
      prefetchData: () => Promise.resolve({ success: false }),
      clearData: () => Promise.resolve(),
      dismissBanner: () => {},
      refreshPending: () => Promise.resolve()
    };
  }
  return context;
}

/**
 * Hook for offline-aware data fetching
 */
export function useOfflineData(endpoint, options = {}) {
  const { enabled = true, refetchOnReconnect = true } = options;
  const { isOnline, recentlyReconnected, initialized } = useOffline();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [isCached, setIsCached] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!enabled) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const api = await getOfflineApi();
      const result = await api.get(endpoint, { forceRefresh });
      
      if (mountedRef.current) {
        setData(result);
        setIsCached(!!result?._cached);
        setIsStale(!!result?._stale);
        setIsRevalidating(!!result?._revalidating);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [endpoint, enabled]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    if (initialized || !enabled) {
      fetchData();
    }
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, initialized, enabled]);

  // Refetch when coming back online
  useEffect(() => {
    if (recentlyReconnected && refetchOnReconnect && !loading && initialized) {
      fetchData(true);
    }
  }, [recentlyReconnected, refetchOnReconnect, fetchData, loading, initialized]);

  return {
    data,
    loading,
    error,
    isStale,
    isCached,
    isRevalidating,
    isOffline: !isOnline,
    refetch: () => fetchData(true),
    refresh: fetchData
  };
}

/**
 * Hook for offline-aware mutations
 */
export function useOfflineMutation(endpoint, options = {}) {
  const { 
    method = 'POST', 
    queueIfOffline = true,
    onSuccess,
    onError,
    onQueued 
  } = options;
  
  const { isOnline, refreshPending } = useOffline();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queued, setQueued] = useState(false);

  const mutate = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    setQueued(false);
    
    try {
      const api = await getOfflineApi();
      const result = await api[method.toLowerCase()](endpoint, data, {
        queueIfOffline,
        onQueued: (queueId) => {
          setQueued(true);
          refreshPending();
          onQueued?.(queueId);
        }
      });
      
      if (result.queued) {
        return result;
      }
      
      onSuccess?.(result.data);
      return result;
    } catch (err) {
      setError(err);
      onError?.(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [endpoint, method, queueIfOffline, onSuccess, onError, onQueued, refreshPending]);

  return {
    mutate,
    loading,
    error,
    queued,
    isOffline: !isOnline
  };
}

/**
 * Hook for saving drafts
 */
export function useDraft(type, id) {
  const [draft, setDraft] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);

  // Load draft on mount
  useEffect(() => {
    if (!id) return;
    
    const loadDraft = async () => {
      try {
        const storage = await getOfflineStorage();
        const savedDraft = await storage.getDraft(type, id);
        if (savedDraft) {
          setDraft(savedDraft);
          setHasDraft(true);
        }
      } catch (err) {
        logger.error('Failed to load draft:', err);
      }
    };
    
    loadDraft();
  }, [type, id]);

  // Save draft
  const saveDraft = useCallback(async (data) => {
    if (!id) return;
    try {
      const storage = await getOfflineStorage();
      await storage.saveDraft(type, id, data);
      setDraft(data);
      setHasDraft(true);
    } catch (err) {
      logger.error('Failed to save draft:', err);
    }
  }, [type, id]);

  // Clear draft
  const clearDraft = useCallback(async () => {
    if (!id) return;
    try {
      const storage = await getOfflineStorage();
      await storage.deleteDraft(type, id);
      setDraft(null);
      setHasDraft(false);
    } catch (err) {
      logger.error('Failed to clear draft:', err);
    }
  }, [type, id]);

  return {
    draft,
    hasDraft,
    saveDraft,
    clearDraft
  };
}

export default {
  OfflineProvider,
  useOffline,
  useOfflineData,
  useOfflineMutation,
  useDraft
};
